import { apolloClientToProps, initializeApollo } from "../lib/apollo";
import getTranslations from "../lib/getTranslations";
import { composeProps } from "@/common/utils/compose-props";
import MainLayout from "@/components/Layouts/MainLayout";
import { CURATED_NEW_IN_ENABLED } from "@/config";
import { fetchHomepageNewThisWeek } from "@/features/search/api/new-this-week-curated";
import brandsQuery from "@whoppah/apollo/query/brands.graphql";
import collectionsQuery from "@whoppah/apollo/query/collections.graphql";
import homepageQuery from "@whoppah/apollo/query/homepage/homepage.graphql";
import homepageCategoriesQuery from "@whoppah/apollo/query/homepageCategories.graphql";
import { Homepage as Page } from "@whoppah/components/Homepage/Homepage";
import { HOMEPAGE_MARKETING_COLLECTION_PAGE_NAME } from "@whoppah/components/Homepage/hooks/useHomepageMarketingCollection.hooks";
import LanguageUtils from "@whoppah/utils/LanguageUtils";

const Homepage = ({ newThisWeek }) => {
  return <Page newThisWeek={newThisWeek} />;
};

Homepage.getLayout = page => {
  return <MainLayout fullWidthContainer>{page}</MainLayout>;
};

const getHomePageDataFromBackend = async ({ locale }) => {
  const client = initializeApollo();
  const language = LanguageUtils.getLanguage(locale);

  await Promise.all([
    // The mobile header's category strip (HeaderMobileCategories) reads this
    // query. Without it in the static props the strip renders nothing on the
    // server and pops in after the client fetch, pushing the hero down (CLS).
    client.query({
      query: homepageQuery,
      variables: {
        lang: language,
      },
    }),
    client.query({
      query: homepageCategoriesQuery,
      variables: {
        lang: language,
      },
    }),
    client.query({
      query: collectionsQuery,
      variables: {
        name: HOMEPAGE_MARKETING_COLLECTION_PAGE_NAME,
        lang: language,
      },
    }),
    // HomepagePopularBrandsBlock (useHomepagePopularBrands) reads this. Without
    // it the block server-rendered its heading and nothing else: the eight brand
    // links and logos existed only after the client fetch resolved, so every
    // crawler that does not run JS saw an empty section, and every visitor paid
    // a round trip for markup we already had at build time. `filters` must stay
    // identical to the hook's or Apollo keys a separate ROOT_QUERY field and
    // fetches it again anyway.
    client.query({
      query: brandsQuery,
      variables: {
        filters: {
          superPopular: true,
        },
        lang: language,
      },
    }),
  ]);

  return apolloClientToProps(client);
};

// The "New this week" section: the twelve newest live listings plus a few
// spares (the client drops what For-you already shows), baked into the ISR
// HTML. With NEXT_PUBLIC_CURATED_NEW_IN on it reads the gateway's curated
// new-in feed and falls back to search. Resolves to an empty list on any
// failure, so it never fails a build.
const homePageNewThisWeek = async ({ locale }) => ({
  props: {
    newThisWeek: await fetchHomepageNewThisWeek({
      curated: CURATED_NEW_IN_ENABLED,
      client: initializeApollo(),
      language: LanguageUtils.getLanguage(locale),
      lang: locale ?? "en",
    }),
  },
});

const homePageTranslations = async ({ locale }) => {
  return {
    props: await getTranslations(locale, [
      "home",
      "homepage",
      "category",
      "b2b",
      // MissingPaymentInfoModal (useShowMissingPaymentInfoModal) reads it.
      "account-payment",
      // OpenHomePromoBlock (Open Home demo branch).
      "open-home",
    ]),
  };
};

export const getStaticProps = composeProps(
  getHomePageDataFromBackend,
  homePageNewThisWeek,
  homePageTranslations
);

export default Homepage;
